/**
 * Ring PRO "Ships by" ETA helpers.
 *
 * The date itself now comes from the ops backend feed
 * (`GET /api/web_v1/ring_sku_estimates_v2?store=...`, see
 * components/ring-air/utils/estimates.ts), keyed by an ops SKU code. This
 * module only builds that key and holds the fallback offset used when the
 * feed has no entry (fetch failure, timeout, or an unmapped SKU).
 */
import { RingProColors, RingSizes } from './constants';

/** Fallback when the backend feed has no usable entry for the selection. */
export const RING_PRO_FALLBACK_SHIP_BY_OFFSET_DAYS = 15;

const OPS_COLOR_LETTER: Record<RingProColors, string> = {
  [RingProColors.BLACK]: 'A',
  [RingProColors.GOLD]: 'G',
  [RingProColors.TITANIUM]: 'R',
  [RingProColors.SILVER]: 'S',
};

/** Ops SKU code = P + color letter (A Aster Black, G Bionic Gold,
 *  R Raw Titanium, S Space Silver) + 2-digit size. Open size (sizing kit)
 *  maps to '00'. */
export function ringProOpsSkuCode(
  color: RingProColors,
  size: RingSizes,
): string {
  const sizeCode =
    size === RingSizes.OPEN ? '00' : String(size).padStart(2, '0');
  return `P${OPS_COLOR_LETTER[color]}${sizeCode}`;
}

/** One feed entry; the live payload carries null dates for some keys. */
export type RingProShipByEstimateEntry = {
  date: string | null;
  mcf_for_eta: boolean;
  show_mcf_logo: boolean;
  default_custom_eta: string | null;
};

/**
 * Resolve the ship-by Date for a selection. The backend entry wins
 * (`date` on the MCF fast path when `mcfValid`, else `default_custom_eta`)
 * only when it parses AND is not before `base`'s calendar day — a stale feed
 * must never promise a date already in the past, which the old
 * today-plus-offset logic structurally couldn't. Everything else falls back
 * to base + RING_PRO_FALLBACK_SHIP_BY_OFFSET_DAYS.
 */
export function resolveRingProShipByDate(
  entry: RingProShipByEstimateEntry | undefined,
  mcfValid: boolean,
  base: Date,
): Date {
  const backendDateStr = entry
    ? mcfValid && entry.mcf_for_eta
      ? entry.date
      : entry.default_custom_eta
    : null;
  // Only the bare YYYY-MM-DD form is accepted: other shapes ('12/10/2026',
  // '2027', datetime-without-offset) parse timezone- or locale-dependently.
  if (backendDateStr && /^\d{4}-\d{2}-\d{2}$/.test(backendDateStr)) {
    const backendDate = new Date(backendDateStr);
    // Feed dates are bare YYYY-MM-DD (parsed as UTC midnight) and the line is
    // DISPLAYED in Asia/Kolkata, so "not in the past" must mean the IST
    // calendar day — a UTC-day comparison leaves a 5.5 h nightly window where
    // a one-day-stale date still renders.
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
    })
      .format(base)
      .split('-')
      .map(Number);
    const baseIstDay = Date.UTC(y, m - 1, d);
    if (!isNaN(backendDate.getTime()) && backendDate.getTime() >= baseIstDay) {
      return backendDate;
    }
  }
  return new Date(
    base.getTime() + RING_PRO_FALLBACK_SHIP_BY_OFFSET_DAYS * 86_400_000,
  );
}
